import React, { useEffect, useState } from "react"
import { useNavigate, useSearchParams, useLocation } from "react-router-dom"
import { Typography, Box, CircularProgress } from "@mui/material"
import { Logger } from "../../../../lib/logging"
import { usePublicApiMutation } from "../../../../core/api/useApiMutation"
import { authenticateByActivationCode } from "../../loginApi"
import {
    AuthenticationTokenType,
    isAuthenticationComplete,
    isRequiresCreatePasswordResponse,
    toAuthenticationTokenType,
} from "../../loginTypes"
import { CreatePasswordState } from "../../pages/CreatePassword"
import { ACTIVATION_CODE_ROUTE, CREATE_PASSWORD_ROUTE, ROOT_ROUTE } from "../../../../constants/routes"
import { LocalizedStrict } from "../../../localization/components/AppLocalized"
import { useAppDispatch } from "../../../store/storeHooks"
import { login } from "../../loginSlice"
import { useTimeoutRender } from "../../../../shared/hooks/useTimeoutRender"

const autoActivateDelayMillis = 1500

type AutoLoginProps = {
    token: string
    tokenType: AuthenticationTokenType
    originalUrl?: string
}

const AutoLoginContent = ({ token, tokenType, originalUrl }: AutoLoginProps) => {
    const logger = new Logger("auto-login")
    const navigate = useNavigate()
    const shouldRender = useTimeoutRender()
    const [hasAttemptedLogin, setHasAttemptedLogin] = useState(false)
    const dispatch = useAppDispatch()

    const { mutateAsync: callAuthenticateByActivationCode } = usePublicApiMutation(authenticateByActivationCode, {
        async onError(error) {
            logger.error("Auto-login failed", error)
            navigate(`${ACTIVATION_CODE_ROUTE}?otp=${token}`)
        },
    })

    useEffect(() => {
        if (hasAttemptedLogin) return

        const performAutoLogin = async () => {
            try {
                logger.info(`Attempting auto-login with token type ${tokenType}`)
                const response = await callAuthenticateByActivationCode({
                    type: tokenType,
                    value: token,
                })

                if (isRequiresCreatePasswordResponse(response)) {
                    const navState: CreatePasswordState = {
                        user: response?.user,
                        oneTimeToken: response?.authentication?.token,
                        redirectUrl: originalUrl,
                    }
                    navigate(CREATE_PASSWORD_ROUTE, { state: navState })
                } else if (isAuthenticationComplete(response)) {
                    dispatch(
                        login({
                            authToken: response.authentication.token,
                            user: response.user,
                        }),
                    )

                    navigate(originalUrl ?? ROOT_ROUTE)
                } else {
                    logger.error(`Unexpected response from auto-login: ${response?.authenticationResult?.action}`)
                    navigate(`${ACTIVATION_CODE_ROUTE}?otp=${token}`)
                }
            } catch (error) {
                logger.error("Auto-login error", error)
            }
        }

        const timeoutId = setTimeout(() => {
            setHasAttemptedLogin(true)
            performAutoLogin()
        }, autoActivateDelayMillis)

        return () => clearTimeout(timeoutId)
    }, [token, tokenType, hasAttemptedLogin, callAuthenticateByActivationCode, navigate, dispatch, originalUrl])

    if (!shouldRender) return null

    return (
        <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            minHeight="50vh"
            textAlign="center"
            padding={3}
        >
            <CircularProgress size={48} sx={{ marginBottom: 3 }} />
            <LocalizedStrict id="auto-login-title">
                <Typography variant="h5" component="h1" gutterBottom>
                    Signing you in...
                </Typography>
            </LocalizedStrict>
            <LocalizedStrict id="auto-login-description">
                <Typography variant="body1" color="text.secondary">
                    Please wait while we authenticate you automatically.
                </Typography>
            </LocalizedStrict>
        </Box>
    )
}

export const AutoLogin = () => {
    const [searchParams] = useSearchParams()
    const location = useLocation()
    const logger = new Logger("auto-login")

    const token = searchParams.get("token") || searchParams.get("otp")
    const tokenType = toAuthenticationTokenType(searchParams.get("type")) ?? AuthenticationTokenType.ACTIVATION_CODE

    const originalUrl = location.state?.originalUrl

    if (!token) {
        logger.error("AutoLogin component rendered without token")
        return (
            <Box padding={3} textAlign="center">
                <LocalizedStrict id="auto-login-error-title">
                    <Typography variant="h6" color="error">
                        Authentication token not found
                    </Typography>
                </LocalizedStrict>
                <LocalizedStrict id="auto-login-error-description">
                    <Typography variant="body2" color="text.secondary">
                        Please check your link and try again.
                    </Typography>
                </LocalizedStrict>
            </Box>
        )
    }

    return <AutoLoginContent token={token} tokenType={tokenType} originalUrl={originalUrl} />
}
